import React, { useState, useEffect } from 'react';
import { equipesAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Users, UserCheck, ShieldCheck } from 'lucide-react';

const Equipes = () => {
  const [equipes, setEquipes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [equipeEditando, setEquipeEditando] = useState(null);

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    supervisor_id: '',
    supervisor_nome: '',
    membros_ids: []
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [eqRes, usRes] = await Promise.all([
        equipesAPI.listar(),
        usersAPI.list()
      ]);
      setEquipes(eqRes.data);
      setUsuarios(usRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const supervisores = usuarios.filter(u => u.perfil === 'SUPERVISOR' && u.ativo);
  const vendedores = usuarios.filter(u => u.perfil === 'VENDEDOR' && u.ativo);

  const abrirModalNovo = () => {
    setEquipeEditando(null);
    setFormData({ nome: '', descricao: '', supervisor_id: '', supervisor_nome: '', membros_ids: [] });
    setModalAberto(true);
  };

  const abrirModalEditar = (equipe) => {
    setEquipeEditando(equipe);
    setFormData({
      nome: equipe.nome,
      descricao: equipe.descricao || '',
      supervisor_id: equipe.supervisor_id || '',
      supervisor_nome: equipe.supervisor_nome || '',
      membros_ids: equipe.membros_ids || []
    });
    setModalAberto(true);
  };

  const handleSalvar = async () => {
    if (!formData.nome.trim()) {
      toast.error('Nome da equipe e obrigatorio');
      return;
    }

    setSalvando(true);
    try {
      if (equipeEditando) {
        await equipesAPI.atualizar(equipeEditando.id, formData);
        toast.success('Equipe atualizada!');
      } else {
        await equipesAPI.criar(formData);
        toast.success('Equipe criada!');
      }
      setModalAberto(false);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (equipe) => {
    if (!window.confirm(`Excluir equipe "${equipe.nome}"? Os vendedores desta equipe ficarao sem equipe.`)) return;

    try {
      await equipesAPI.excluir(equipe.id);
      toast.success('Equipe excluida!');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const getNomeMembro = (id) => {
    const u = usuarios.find(u => u.id === id);
    return u?.nome || 'Desconhecido';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="h-7 w-7 text-red-600" />
            Equipes de Vendas
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Gerencie as equipes, supervisores e colaboradores
          </p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={abrirModalNovo} data-testid="btn-nova-equipe">
          <Plus className="mr-2 h-4 w-4" />
          Nova Equipe
        </Button>
      </div>

      {/* Lista */}
      <div>
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" />
          </div>
        ) : equipes.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhuma equipe cadastrada</p>
              <Button variant="outline" className="mt-4" onClick={abrirModalNovo}>
                <Plus className="mr-2 h-4 w-4" />
                Criar primeira equipe
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {equipes.map((equipe) => (
              <Card key={equipe.id} className="border-gray-200 dark:border-gray-700" data-testid={`equipe-card-${equipe.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{equipe.nome}</CardTitle>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => abrirModalEditar(equipe)} data-testid={`btn-editar-equipe-${equipe.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleExcluir(equipe)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {equipe.descricao && (
                    <p className="text-sm text-gray-500 mb-3">{equipe.descricao}</p>
                  )}

                  {/* Supervisor */}
                  {equipe.supervisor_nome && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <ShieldCheck className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        Supervisor: {equipe.supervisor_nome}
                      </span>
                    </div>
                  )}

                  {/* Membros */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                      <UserCheck className="h-3.5 w-3.5" />
                      Colaboradores ({(equipe.membros_ids || []).length})
                    </p>
                    {(equipe.membros_ids || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {(equipe.membros_ids || []).map(id => (
                          <Badge key={id} variant="secondary" className="text-xs">
                            {getNomeMembro(id)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Nenhum colaborador</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-red-600" />
              {equipeEditando ? 'Editar Equipe' : 'Nova Equipe'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome da Equipe <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Equipe Alfa"
                  className="mt-1"
                  data-testid="equipe-nome-input"
                />
              </div>
              <div>
                <Label>Descricao</Label>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Descricao da equipe"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Supervisor */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <Label className="text-yellow-800 dark:text-yellow-300 font-semibold flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> Supervisor Responsavel
              </Label>
              <Select
                value={formData.supervisor_id || 'nenhum'}
                onValueChange={(val) => {
                  if (val === 'nenhum') {
                    setFormData({ ...formData, supervisor_id: '', supervisor_nome: '' });
                  } else {
                    const sup = supervisores.find(s => s.id === val);
                    setFormData({ ...formData, supervisor_id: val, supervisor_nome: sup?.nome || '' });
                  }
                }}
              >
                <SelectTrigger className="mt-2" data-testid="equipe-supervisor-select">
                  <SelectValue placeholder="Selecione o supervisor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                  {supervisores.map(sup => (
                    <SelectItem key={sup.id} value={sup.id}>{sup.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {supervisores.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">Nenhum supervisor cadastrado. Crie um usuario com perfil SUPERVISOR primeiro.</p>
              )}
            </div>

            {/* Membros (Colaboradores) */}
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
              <Label className="text-red-800 dark:text-red-300 font-semibold flex items-center gap-2 mb-3">
                <Users className="h-4 w-4" /> Colaboradores da Equipe
              </Label>
              <p className="text-xs text-red-600 dark:text-red-400 mb-3">Selecione os vendedores que fazem parte desta equipe:</p>
              {vendedores.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {vendedores.map(v => (
                    <div key={v.id} className="flex items-center space-x-2 p-2 bg-white dark:bg-gray-800 rounded border">
                      <Checkbox
                        id={`membro-eq-${v.id}`}
                        checked={formData.membros_ids.includes(v.id)}
                        onCheckedChange={(checked) => {
                          setFormData({
                            ...formData,
                            membros_ids: checked
                              ? [...formData.membros_ids, v.id]
                              : formData.membros_ids.filter(id => id !== v.id)
                          });
                        }}
                      />
                      <label htmlFor={`membro-eq-${v.id}`} className="text-sm font-medium cursor-pointer flex-1">
                        {v.nome}
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-amber-600">Nenhum vendedor cadastrado.</p>
              )}
              <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                Selecionados: {formData.membros_ids.length} colaborador(es)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSalvar} disabled={salvando} className="bg-red-600 hover:bg-red-700" data-testid="btn-salvar-equipe">
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {equipeEditando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Equipes;
